import { createContext, useContext, useMemo } from 'react'
import { Outlet, useParams, useNavigate, NavLink } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import * as orgsApi from '@/lib/api/orgs'
import type { OrgListItem, OrganizationWithCounts } from '@/lib/api/orgs'
import * as productsApi from '@/lib/api/products'
import type { ProductWithTypes } from '@/lib/api/products'
import { queryKeys } from '@/lib/queryKeys'
import { TopNav } from '@/components/layout/TopNav'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ChevronDown,
  Plus,
  Settings,
  CreditCard,
  LayoutDashboard,
  ClipboardList,
  FileText,
  Code2,
  FileCheck2,
  FlaskConical,
  BarChart2,
  AlertTriangle,
  Table2,
  Download,
  Upload,
  Shield,
  ShieldCheck,
  ScrollText,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useBilling } from '@/hooks/useBilling'
import { useProductModules } from '@/hooks/useProductModules'
import type { ProductModule } from '@reqtrack/shared'
import { BillingGuard } from '@/components/BillingGuard'
import { TrialBanner } from '@/components/TrialBanner'
import { CancellingBanner } from '@/components/CancellingBanner'
import { useOrgHasModule } from '@/hooks/useOrgHasModule'

// ─── Contexts ────────────────────────────────────────────────────────────────

export interface OrgContextValue {
  org: OrganizationWithCounts | null
  orgListItem: OrgListItem | null
  orgId: string
  orgSlug: string
}

export interface ProductContextValue {
  product: ProductWithTypes | null
  productId: string
  orgId: string
  orgSlug: string
}

const OrgContext = createContext<OrgContextValue | null>(null)
const ProductContext = createContext<ProductContextValue | null>(null)

export function useOrgContext(): OrgContextValue {
  const ctx = useContext(OrgContext)
  if (!ctx) throw new Error('useOrgContext must be used within AppLayout')
  return ctx
}

export function useProductContext(): ProductContextValue {
  const ctx = useContext(ProductContext)
  if (!ctx) throw new Error('useProductContext must be used within AppLayout')
  return ctx
}

// ─── Nav sections (product-scoped) ───────────────────────────────────────────

interface NavItem {
  label: string
  to: string
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>
  /** If set, item is only shown when this module is enabled */
  module?: string
}

interface NavSection {
  label: string
  items: NavItem[]
}

function productNavSections(base: string, options: { softwareOnly?: boolean } = {}): NavSection[] {
  const { softwareOnly = false } = options
  return [
    {
      label: 'Requirements',
      items: [
        { label: 'User Requirements', to: `${base}/user-requirements`, icon: ClipboardList },
        { label: softwareOnly ? 'Software Requirements' : 'Product Requirements', to: `${base}/product-requirements`, icon: FileText },
        ...(!softwareOnly ? [{ label: 'Sub-Requirements', to: `${base}/sub-requirements`, icon: Code2 }] : []),
      ],
    },
    {
      label: 'Evidence and Outputs',
      items: [{ label: 'Records', to: `${base}/records`, icon: FileCheck2 }],
    },
    {
      label: 'Testing',
      items: [
        { label: 'Test Protocols', to: `${base}/test-protocols`, icon: FlaskConical, module: 'testProtocols' },
        { label: 'Coverage', to: `${base}/coverage`, icon: BarChart2, module: 'testProtocols' },
      ],
    },
    {
      label: 'Risk',
      items: [{ label: 'Hazards', to: `${base}/hazards`, icon: AlertTriangle, module: 'hazardAnalysis' }],
    },
    {
      label: 'Views',
      items: [
        ...(!softwareOnly ? [{ label: 'Trace Matrix', to: `${base}/trace-matrix`, icon: Table2 }] : []),
        {
          label: softwareOnly ? 'Trace Matrix' : 'Software Trace Matrix',
          to: `${base}/software-trace-matrix`,
          icon: Table2,
          module: 'softwareTraceability',
        },
      ],
    },
    {
      label: 'Exports & Import',
      items: [
        { label: 'Exports', to: `${base}/exports`, icon: Download },
        { label: 'Import', to: `${base}/import`, icon: Upload },
      ],
    },
    {
      label: 'Security',
      items: [
        { label: 'Controls Coverage', to: `${base}/controls-coverage`, icon: ShieldCheck, module: 'controlsTraceability' },
      ],
    },
    {
      label: 'Product',
      items: [
        { label: 'Settings', to: `${base}/settings`, icon: Settings },
      ],
    },
  ]
}

// ─── AppLayout ────────────────────────────────────────────────────────────────

export function AppLayout() {
  const { orgSlug = '', productId = '' } = useParams<{ orgSlug: string; productId: string }>()
  const navigate = useNavigate()

  // Org data
  const orgsQuery = useQuery({
    queryKey: queryKeys.orgs.all(),
    queryFn: orgsApi.getOrgs,
  })
  const orgListItem = orgsQuery.data?.data.find((o) => o.slug === orgSlug) ?? null
  const orgId = orgListItem?.id ?? ''

  const orgDetailQuery = useQuery({
    queryKey: queryKeys.orgs.detail(orgId),
    queryFn: () => orgsApi.getOrg(orgId),
    enabled: !!orgId,
  })
  const org = orgDetailQuery.data?.data ?? null

  // Products list
  const productsQuery = useQuery({
    queryKey: queryKeys.orgs.products(orgId),
    queryFn: () => productsApi.getProducts(orgId),
    enabled: !!orgId,
  })
  const products = productsQuery.data?.data ?? []

  // Current product detail (only when productId is in URL)
  const productQuery = useQuery({
    queryKey: queryKeys.products.detail(productId),
    queryFn: () => productsApi.getProduct(orgId, productId),
    enabled: !!orgId && !!productId,
  })
  const product = productId ? (productQuery.data?.data ?? null) : null

  const { isTrialing, isCancelling, daysRemaining, currentPeriodEndsAt } = useBilling(orgId)
  const { hasModule } = useProductModules(productId || undefined)
  const productIdsList = useMemo(() => products.map((p) => p.id), [products])
  const orgHasControls = useOrgHasModule(orgId, productIdsList, 'controlsTraceability')
  const userIsAdmin = orgListItem?.role === 'ADMIN' || orgListItem?.role === 'OWNER'

  const base = `/orgs/${orgSlug}/products/${productId}`
  const rawNavSections = productId ? productNavSections(base, { softwareOnly: product?.softwareOnly ?? false }) : []
  // Filter sections: hide items that require a module that isn't enabled.
  // Hide the whole section if all its items are hidden.
  const navSections = rawNavSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.module || hasModule(item.module as ProductModule)),
    }))
    .filter((section) => section.items.length > 0)

  const orgCtx: OrgContextValue = { org, orgListItem, orgId, orgSlug }
  const productCtx: ProductContextValue = { product, productId, orgId, orgSlug }

  return (
    <OrgContext.Provider value={orgCtx}>
      <ProductContext.Provider value={productCtx}>
        <div className="flex flex-col min-h-screen">
          {/* Single top nav */}
          <TopNav
            orgSlug={orgSlug}
            orgName={org?.name ?? orgListItem?.name}
            productId={productId || undefined}
            productName={product?.name}
          />

          {isTrialing && (
            <TrialBanner orgId={orgId} orgSlug={orgSlug} daysRemaining={daysRemaining} />
          )}
          {isCancelling && (
            <CancellingBanner orgId={orgId} orgSlug={orgSlug} currentPeriodEndsAt={currentPeriodEndsAt} />
          )}

          <div className="flex flex-1 min-h-0">
            {/* ── Sidebar — Forge dark warm graphite ── */}
            <aside
              className="w-64 flex flex-col shrink-0"
              style={{ background: 'hsl(var(--sidebar-bg))', borderRight: '1px solid hsl(var(--sidebar-border))' }}
            >
              {/* Product switcher */}
              <div className="p-3" style={{ borderBottom: '1px solid hsl(var(--sidebar-border))' }}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="w-full justify-between h-auto px-3 py-2 font-normal hover:bg-transparent"
                      style={{
                        color: 'hsl(var(--sidebar-fg))',
                        border: '1px solid hsl(var(--sidebar-border))',
                        borderRadius: 'var(--radius)',
                        background: 'hsl(var(--sidebar-hover))',
                      }}
                    >
                      {productsQuery.isLoading ? (
                        <Skeleton className="h-4 w-32 opacity-30" />
                      ) : product ? (
                        <span className="flex items-center gap-2 min-w-0">
                          <Badge
                            variant="outline"
                            className="text-xs shrink-0 font-mono font-semibold border-0"
                            style={{
                              background: 'hsl(var(--copper-tint))',
                              color: 'hsl(var(--copper-tint-fg))',
                            }}
                          >
                            {product.prefix}
                          </Badge>
                          <span className="truncate text-sm font-medium">{product.name}</span>
                        </span>
                      ) : (
                        <span className="text-sm" style={{ color: 'hsl(var(--sidebar-muted))' }}>
                          Select a product…
                        </span>
                      )}
                      <ChevronDown className="h-4 w-4 shrink-0" style={{ color: 'hsl(var(--sidebar-muted))' }} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-60" align="start">
                    <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                      {org?.name ?? orgSlug}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {products.length === 0 ? (
                      <DropdownMenuItem disabled>No products yet</DropdownMenuItem>
                    ) : (
                      products.map((p) => (
                        <DropdownMenuItem
                          key={p.id}
                          onClick={() => navigate(`/orgs/${orgSlug}/products/${p.id}/`)}
                          className={cn(p.id === productId && 'bg-accent')}
                        >
                          <Badge variant="outline" className="text-xs mr-2 shrink-0 font-mono">
                            {p.prefix}
                          </Badge>
                          <span className="truncate">{p.name}</span>
                        </DropdownMenuItem>
                      ))
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate(`/orgs/${orgSlug}/products/new`)}>
                      <Plus className="h-4 w-4 mr-2" />
                      New product
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Product nav (only when a product is selected) */}
              {productId ? (
                <nav className="flex-1 overflow-y-auto py-2 px-2">
                  {navSections.map((section) => (
                    <div key={section.label} className="mb-3">
                      <p
                        className="px-3 py-1 text-xs font-semibold uppercase tracking-widest mb-0.5"
                        style={{ color: 'hsl(var(--sidebar-muted))' }}
                      >
                        {section.label}
                      </p>
                      {section.items.map((item) => {
                        const Icon = item.icon
                        return (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            className={({ isActive }) =>
                              cn(
                                'flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors',
                                isActive
                                  ? 'font-semibold'
                                  : 'hover:opacity-100'
                              )
                            }
                            style={({ isActive }) => ({
                              color: isActive ? 'hsl(var(--sidebar-active-fg))' : 'hsl(var(--sidebar-fg))',
                              background: isActive ? 'hsl(var(--sidebar-active-bg))' : 'transparent',
                              opacity: isActive ? 1 : undefined,
                            })}
                            onMouseEnter={(e) => {
                              const el = e.currentTarget
                              if (!el.dataset.active) {
                                el.style.background = 'hsl(var(--sidebar-hover))'
                              }
                            }}
                            onMouseLeave={(e) => {
                              const el = e.currentTarget
                              if (!el.dataset.active) {
                                el.style.background = 'transparent'
                              }
                            }}
                          >
                            {({ isActive }) => (
                              <>
                                <Icon
                                  className="h-4 w-4 shrink-0"
                                  style={{ opacity: isActive ? 1 : 0.7 }}
                                />
                                {item.label}
                              </>
                            )}
                          </NavLink>
                        )
                      })}
                    </div>
                  ))}
                </nav>
              ) : (
                /* Org dashboard nav when no product selected */
                <nav className="flex-1 overflow-y-auto py-2 px-2">
                  <NavLink
                    to={`/orgs/${orgSlug}`}
                    end
                    className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors"
                    style={({ isActive }) => ({
                      color: isActive ? 'hsl(var(--sidebar-active-fg))' : 'hsl(var(--sidebar-fg))',
                      background: isActive ? 'hsl(var(--sidebar-active-bg))' : 'transparent',
                      fontWeight: isActive ? 600 : 400,
                    })}
                  >
                    <LayoutDashboard className="h-4 w-4 shrink-0 opacity-80" />
                    Dashboard
                  </NavLink>
                </nav>
              )}

              {/* Org admin links — pinned to bottom */}
              <div style={{ borderTop: '1px solid hsl(var(--sidebar-border))' }} className="pb-1">
                <div className="px-3 py-2">
                  <p
                    className="text-xs font-semibold uppercase tracking-widest truncate"
                    style={{ color: 'hsl(var(--sidebar-muted))' }}
                  >
                    {org?.name ?? orgSlug}
                  </p>
                </div>
                <div className="px-2">
                  <NavLink
                    to={`/orgs/${orgSlug}/settings`}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors"
                    style={({ isActive }) => ({
                      color: isActive ? 'hsl(var(--sidebar-active-fg))' : 'hsl(var(--sidebar-fg))',
                      background: isActive ? 'hsl(var(--sidebar-active-bg))' : 'transparent',
                      fontWeight: isActive ? 600 : 400,
                    })}
                  >
                    <Settings className="h-4 w-4 shrink-0 opacity-70" />
                    Settings
                  </NavLink>
                  <NavLink
                    to={`/orgs/${orgSlug}/billing`}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors"
                    style={({ isActive }) => ({
                      color: isActive ? 'hsl(var(--sidebar-active-fg))' : 'hsl(var(--sidebar-fg))',
                      background: isActive ? 'hsl(var(--sidebar-active-bg))' : 'transparent',
                      fontWeight: isActive ? 600 : 400,
                    })}
                  >
                    <CreditCard className="h-4 w-4 shrink-0 opacity-70" />
                    Billing
                  </NavLink>
                  {orgHasControls && (
                    <NavLink
                      to={`/orgs/${orgSlug}/controls`}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors"
                      style={({ isActive }) => ({
                        color: isActive ? 'hsl(var(--sidebar-active-fg))' : 'hsl(var(--sidebar-fg))',
                        background: isActive ? 'hsl(var(--sidebar-active-bg))' : 'transparent',
                        fontWeight: isActive ? 600 : 400,
                      })}
                    >
                      <Shield className="h-4 w-4 shrink-0 opacity-70" />
                      Controls
                    </NavLink>
                  )}
                  {userIsAdmin && (
                    <NavLink
                      to={`/orgs/${orgSlug}/audit-log`}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors"
                      style={({ isActive }) => ({
                        color: isActive ? 'hsl(var(--sidebar-active-fg))' : 'hsl(var(--sidebar-fg))',
                        background: isActive ? 'hsl(var(--sidebar-active-bg))' : 'transparent',
                        fontWeight: isActive ? 600 : 400,
                      })}
                    >
                      <ScrollText className="h-4 w-4 shrink-0 opacity-70" />
                      Audit Log
                    </NavLink>
                  )}
                </div>
              </div>
            </aside>

            {/* ── Main content ── */}
            <main className="flex-1 p-6 overflow-auto">
              <BillingGuard orgId={orgId} orgSlug={orgSlug}>
                <Outlet />
              </BillingGuard>
            </main>
          </div>
        </div>
      </ProductContext.Provider>
    </OrgContext.Provider>
  )
}
